import { useAuth } from 'hooks/auth';
import { useLayout } from 'hooks/layout';
import { Layouts } from 'pages';
import React, { useMemo } from 'react';
import { Route, Redirect } from 'react-router-dom';
import { useLastLocation } from 'react-router-last-location';
import {
  consumeIntendedLocation,
  saveIntendedLocation,
} from 'services/intendedLocation';
import Can from './Can';

const RouteWrapper: React.FC<any> = ({
  component: Component,
  isPrivate,
  defaultLayout,
  authLayout,
  loginLayout,
  roles,
  ...rest
}) => {
  const { user, skipBilling } = useAuth();
  const { has_free_trial } = useLayout();
  const isAuthenticated = !!user;
  const lastLocation = useLastLocation();

  const Layout = useMemo<any>(() => {
    if (loginLayout) {
      return Layouts.LoginLayout;
    }

    if (isAuthenticated === (isPrivate || defaultLayout) && !authLayout) {
      return Layouts.DefaultLayout;
    }

    return Layouts.AuthLayout;
  }, [isPrivate, defaultLayout, isAuthenticated, loginLayout, authLayout]);

  if (!isAuthenticated && isPrivate) {
    /*
    Redirect to '/' if user is unauthenticated and route is private
    */
    saveIntendedLocation(rest.location);

    return <Redirect to="/" />;
  }

  if (!rest.path || rest.disabled) return <Redirect to="/404" />;

  if (rest.location.pathname.includes('tickets')) {
    return (
      <Redirect to={rest.location.pathname.replace('tickets', 'suporte')} />
    );
  }
  /*
    Special routes logic
  */
  if (user) {
    if (
      !user.is_staff &&
      !user.is_verified &&
      rest.path !== '/phone/validate'
    ) {
      return <Redirect to="/phone/validate" />;
    }

    if (user.is_verified && rest.path === '/phone/validate') {
      return <Redirect to={consumeIntendedLocation() || '/dashboard'} />;
    }

    if (
      !skipBilling &&
      isAuthenticated &&
      !user.is_staff &&
      user.billing_outdated &&
      user.company_slug === 'configr' &&
      rest.path !== '/update/billing'
    ) {
      return <Redirect to="/update/billing" />;
    }

    if (isAuthenticated && rest.path === '/login/2fa') {
      return <Redirect to={consumeIntendedLocation() || '/dashboard'} />;
    }

    if (!has_free_trial && rest.path === '/onboard') {
      return <Redirect to="/dashboard" />;
    }
  }

  if (isAuthenticated && !isPrivate && !defaultLayout) {
    /*
      O consumo acontece durante o render, o que só é seguro porque o app roda
      em React 17 sem StrictMode: um render por commit. Se isso mudar, mover
      para efeito antes de habilitar StrictMode.
    */
    const intendedLocation = consumeIntendedLocation();
    if (intendedLocation) return <Redirect to={intendedLocation} />;

    // páginas do fluxo de entrada (confirmação, senha, cadastro) não são destino depois do login
    const backTo = lastLocation?.pathname;
    if (
      backTo &&
      backTo !== '/' &&
      !['/register', '/email/', '/password/', '/login/'].some(path =>
        backTo.startsWith(path),
      )
    )
      return <Redirect to={lastLocation} />;

    return <Redirect to="/dashboard" />;
  }

  return (
    <Can roles={roles}>
      <Route
        {...rest}
        render={props => (
          <Layout defaultLayout={defaultLayout}>
            <Component {...props} />
          </Layout>
        )}
      />
    </Can>
  );
};

export default RouteWrapper;
